/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import WebViewer from '@pdftron/webviewer';
import { useQuery } from '@tanstack/react-query';
import { diff_match_patch } from 'diff-match-patch';
import { useParams } from 'react-router-dom';

import { getCompareDocxData } from '../../axiosClient/draftApi';
import { convertPDF } from '../../hooks/Helper';
import {
  useGetCompareDraftQuery,
  useGetDocumentQuery,
} from '../../redux/services/draftApi';

const CompareDraft = () => {
  const { id } = useParams();

  const documentContainer = useRef<HTMLDivElement>(null);

  const { data: compareDataPoints } = useQuery({
    queryKey: ['compare-data-points'],
    queryFn: () =>
      getCompareDocxData({
        compare_draft: id,
        type: 'api',
      }),
    select: (response) => response?.results || [],
    enabled: !!id,
  });

  const { data: compareDocx } = useGetCompareDraftQuery(id, { skip: !id });

  const { data: compare_draft } = useGetDocumentQuery(
    {
      draft_id: compareDocx?.compare_draft.id,
    },
    { skip: !compareDocx?.compare_draft }
  );

  const { data: original_draft } = useGetDocumentQuery(
    {
      draft_id: compareDocx?.original_draft.id,
    },
    { skip: !compareDocx?.original_draft }
  );

  const compareDraftFileLink = useMemo(
    () => compare_draft?.presigned_url,
    [compare_draft]
  );

  const originalDraftFileLink = useMemo(
    () => original_draft?.presigned_url,
    [original_draft]
  );

  const compareData = useMemo(() => {
    const result: any = [];
    compareDataPoints?.map((data: any) => {
      const dmp = new diff_match_patch();
      if (data.answer) {
        const d = dmp.diff_main(data.original || '', data.modified || '');
        if (d) {
          dmp.diff_cleanupSemantic(d);

          const ds = dmp.diff_prettyHtml(d);
          const payload = {
            diff_string: ds,
            answer: data.answer,
          };
          result.push(payload);
        }
      }
    });
    return result;
  }, [compareDataPoints]);

  useEffect(() => {
    setTimeout(async () => {
      if (
        documentContainer.current &&
        compareDraftFileLink &&
        originalDraftFileLink
      ) {
        WebViewer(
          {
            fullAPI: true,
            disableMultiViewerComparison: true,
            path: '/webviewer',
            disabledElements: ['header', 'toolsHeader'],
          },
          documentContainer.current
        ).then((instance) => {
          const { UI, Core } = instance;
          const { Annotations } = Core;
          const { Color } = Annotations;

          instance.UI.openElements(['loadingModal']);

          UI.addEventListener(UI.Events.MULTI_VIEWER_READY, async () => {
            const [documentViewer1, documentViewer2] =
              Core.getDocumentViewers();

            const startCompare = async () => {
              const shouldCompare =
                documentViewer1.getDocument() && documentViewer2.getDocument();

              if (shouldCompare) {
                // Check if both documents loaded before comparing
                const beforeColor = new Color(21, 205, 131, 0.4);
                const afterColor = new Color(255, 73, 73, 0.4);
                const options = {
                  beforeColor,
                  afterColor,
                };

                await documentViewer1.startSemanticDiff(
                  documentViewer2,
                  options
                );
              }
            };

            const options1 = {
              filename: compareDocx.original_draft.name,
              extension: 'pdf',
            };
            const options2 = {
              filename: compareDocx.compare_draft.name,
              extension: 'pdf',
            };

            documentViewer1.addEventListener('documentLoaded', startCompare);
            documentViewer2.addEventListener('documentLoaded', startCompare);
            const iframeDoc = instance.UI.iframeWindow.document;

            // //access the container element by id of each view
            const leftView = iframeDoc.getElementById('container1');
            const rightView = iframeDoc.getElementById('container2');

            //drop area is the first child of the container when document is not loaded
            if (leftView?.firstChild) {
              (leftView.firstChild as HTMLElement).style.display = 'none';
            }
            if (rightView?.firstChild) {
              (rightView.firstChild as HTMLElement).style.display = 'none';
            }

            const leftCross = iframeDoc.getElementById('header1');
            const rightCross = iframeDoc.getElementById('header2');

            if (leftCross?.lastChild?.lastChild) {
              (leftCross.lastChild.lastChild as HTMLElement).style.display =
                'none';
            }
            if (rightCross?.lastChild?.lastChild) {
              (rightCross.lastChild.lastChild as HTMLElement).style.display =
                'none';
            }

            const blob1 = await convertPDF(Core, originalDraftFileLink);
            const blob2 = await convertPDF(Core, compareDraftFileLink);

            blob1
              ? documentViewer1.loadDocument(blob1 as Blob, options1)
              : documentViewer1.loadDocument(originalDraftFileLink, {
                  filename: compareDocx.original_draft.name,
                });
            blob2
              ? documentViewer2.loadDocument(blob2 as Blob, options2)
              : documentViewer2.loadDocument(compareDraftFileLink, {
                  filename: compareDocx.compare_draft.name,
                });

            instance.UI.closeElements(['loadingModal']);
          });
          UI.enableFeatures([UI.Feature.MultiViewerMode]);
        });
      }
    }, 200);
  }, [documentContainer.current, compareDraftFileLink, originalDraftFileLink]);

  return (
    <Stack direction="row" gap="8px">
      <Box sx={{ flex: 1 }}>
        <div
          ref={documentContainer}
          id="document-viewer"
          style={{ height: '82vh' }}
        />
      </Box>
      <Stack
        width="350px"
        sx={{
          background: '#88305f1f',
          borderRadius: '8px',
        }}
      >
        <Typography variant="h6" m="8px 16px">
          Change List
        </Typography>
        <Box sx={{ height: '75vh', overflow: 'scroll', padding: '8px' }}>
          {compareData?.map((data: any, index: number) => (
            <Box
              key={index}
              sx={{
                background: '#FFF7FA',
                padding: '8px 16px',
                margin: '8px 0',
                borderRadius: '12px',
                cursor: 'pointer',
              }}
            >
              <Typography
                variant="caption"
                fontWeight="500"
              >{`# ${index + 1}`}</Typography>
              <Box
                className="compare-diff-string"
                dangerouslySetInnerHTML={{
                  __html: data.diff_string,
                }}
              />
              <Typography variant="body2" fontWeight="600" mt={2}>
                Outcome
              </Typography>
              <Typography variant="body2"> {data.answer}</Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Stack>
  );
};

export default CompareDraft;
